import React, { useEffect, useState } from "react";

function Metric({ label, value }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-black/40 p-3">
      <div className="text-[11px] text-zinc-400">{label}</div>
      <div className="mt-1 text-xl font-bold">{value ?? 0}</div>
    </div>
  );
}

export default function AssistDiagnosticsCard() {
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [tab, setTab] = useState("diagnostics");
  const [hidden, setHidden] = useState(false);

  const isAssistMode = localStorage.getItem("assist_mode") === "true";

  useEffect(() => {
    if (!isAssistMode) return;

    loadAll();

    const timer = setInterval(loadAll, 30000);

    return () => clearInterval(timer);
  }, [isAssistMode]);

  const fetchPlatform = async (path) => {
    const token = localStorage.getItem("platform_return_access_token");

    if (!token) return null;

    const res = await fetch(path, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      throw new Error(`${path} failed ${res.status}`);
    }

    return res.json();
  };

  const loadAll = async () => {
    try {
      const tenantSlug = localStorage.getItem("assist_tenant_slug") || "";
      const url = tenantSlug
        ? `/api/platform/assist/diagnostics?tenant_slug=${encodeURIComponent(tenantSlug)}`
        : "/api/platform/assist/diagnostics";
      const d = await fetchPlatform(url);

      if (d?.stats) {
        setStats(d.stats);
      }
    } catch (e) {
      console.error("diagnostics failed", e);
    }

    try {
      const l = await fetchPlatform("/api/platform/assist/live-sessions");

      if (l?.sessions) {
        setSessions(l.sessions);
      }
    } catch (e) {
      console.error("live sessions failed", e);
    }
  };

  if (!isAssistMode || hidden) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-[9998] w-[380px] max-h-[70vh] overflow-hidden rounded-2xl border border-zinc-800 bg-[#111] shadow-2xl text-white">
      <div className="border-b border-zinc-800 px-4 py-3 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold">
            Assist Operations
          </h3>

          <p className="text-xs text-zinc-500">
            Live tenant support console
          </p>
        </div>

        <button
          type="button"
          onClick={() => setHidden(true)}
          className="text-zinc-400 hover:text-white"
        >
          ×
        </button>
      </div>

      <div className="flex border-b border-zinc-800 text-xs">
        <button
          type="button"
          onClick={() => setTab("diagnostics")}
          className={`flex-1 px-3 py-2 ${
            tab === "diagnostics"
              ? "bg-amber-500 text-black font-bold"
              : "text-zinc-400"
          }`}
        >
          Diagnostics
        </button>

        <button
          type="button"
          onClick={() => setTab("live")}
          className={`flex-1 px-3 py-2 ${
            tab === "live"
              ? "bg-amber-500 text-black font-bold"
              : "text-zinc-400"
          }`}
        >
          Live Users ({sessions.length})
        </button>
      </div>

      {tab === "diagnostics" && (
        <div className="grid grid-cols-2 gap-2 p-3 text-sm">
          <Metric label="Tenants" value={stats?.tenants} />
          <Metric label="Users" value={stats?.users} />
          <Metric label="Sites" value={stats?.sites} />
          <Metric label="Active Shifts" value={stats?.active_shifts} />
          <Metric label="Active Patrols" value={stats?.active_patrols} />
          <Metric label="Open Incidents" value={stats?.unresolved_incidents} />
        </div>
      )}

      {tab === "live" && (
        <div className="p-3 space-y-2 overflow-y-auto max-h-[52vh]">
          {sessions.length === 0 && (
            <div className="text-xs text-zinc-500 text-center py-6">
              No active live sessions detected.
            </div>
          )}

          {sessions.map((s) => (
            <div
              key={s.shift_id}
              className="rounded-xl border border-zinc-800 bg-black/40 p-3"
            >
              <div className="flex justify-between gap-2">
                <div>
                  <div className="text-sm font-bold">
                    {s.officer?.name || "Unknown Officer"}
                  </div>

                  <div className="text-[11px] text-zinc-500">
                    {s.officer?.role || "user"} · {s.site?.name || "Unknown Site"}
                  </div>
                </div>

                <span className={`rounded-full px-2 py-0.5 text-[10px] border ${
                  s.live
                    ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
                    : "bg-zinc-500/10 text-zinc-400 border-zinc-500/30"
                }`}>
                  {s.live ? "LIVE" : "RECENT"}
                </span>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
                <div>
                  <span className="text-zinc-500">Shift</span>
                  <div>{s.shift_status || "unknown"}</div>
                </div>

                <div>
                  <span className="text-zinc-500">Patrol</span>
                  <div>{s.patrol_status || "none"}</div>
                </div>

                <div>
                  <span className="text-zinc-500">Checkpoint</span>
                  <div>{s.current_checkpoint || "None"}</div>
                </div>

                <div>
                  <span className="text-zinc-500">GPS Accuracy</span>
                  <div>
                    {s.gps?.accuracy
                      ? `${s.gps.accuracy}m`
                      : "Unknown"}
                  </div>
                </div>
              </div>

              {s.updated_at && (
                <div className="mt-2 text-[10px] text-zinc-600">
                  Updated: {new Date(s.updated_at).toLocaleString("en-AU")}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
